import { forwardRef, type CSSProperties, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { useUiText } from "./uiText";
import { cx } from "./fieldContext";

export interface SpinProps extends HTMLAttributes<HTMLSpanElement> {
  spinning?: boolean;
  size?: "small" | "default" | "large";
  /** Symbolplatz: Theme-Form in Schriftgröße und geerbter Textfarbe. */
  inline?: boolean;
  tip?: ReactNode;
  /** Antd 6: `description`. */
  description?: ReactNode;
  indicator?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/**
 * Ladeanzeige des Kits (Ersatz für Antd `Spin`). Mit Kindern dimmt sie den Inhalt, statt ihn zu
 * verdecken (Lade-Standard: Struktur zuerst, Daten dimmen).
 */
export const Spin = forwardRef<HTMLElement, SpinProps>(function Spin({ spinning = true, size = "default", inline = false, tip, description, indicator, className, style, children, ...rest }, ref) {
  const { text } = useUiText();
  const label = description ?? tip;
  const mark = (
    <span {...(children == null ? rest : {})} ref={children == null ? ref as Ref<HTMLSpanElement> : undefined} className={cx("ck-spin", inline && "ck-spin--inline", !inline && size === "small" && "ck-spin--sm", !inline && size === "large" && "ck-spin--lg", children == null && className)} style={children == null ? style : undefined} role="status" aria-label={text("Loading...")}>
      {indicator ?? <span className="ck-spin-bars" aria-hidden="true"><i /><i /><i /></span>}
      {label != null ? <span className="ck-spin-tip">{label}</span> : null}
    </span>
  );
  if (children == null) return spinning ? mark : null;
  return (
    <div {...rest} ref={ref as Ref<HTMLDivElement>} className={cx("ck-spin-container", spinning && "is-spinning", className)} style={style} aria-busy={spinning || undefined}>
      {spinning ? <div className="ck-spin-layer">{mark}</div> : null}
      <div className="ck-spin-content">{children}</div>
    </div>
  );
});
